import { axiosInstance } from "@/utils/axios/axiosConfig";
import {
  ShoppingItem,
  ShoppingItemCreate,
  ShoppingList,
  ShoppingListCreate,
} from "@/pages/ShoppingList/types.ts";
import { FamilyMember } from "@/store/auth/authSlice.ts";

const BASE_URL = "/shopping-lists";
const HEADERS = {
  headers: {
    "Content-Type": "application/json",
    Authorization: localStorage.getItem("authorization") || "",
  },
};

export const shoppingListApi = {
  // Récupérer toutes les listes de courses de l'utilisateur
  getLists: async (): Promise<ShoppingList[]> => {
    try {
      const response = await axiosInstance.get(BASE_URL, HEADERS);
      return response.data;
    } catch (error) {
      console.error(
        "Erreur lors de la récupération des listes de courses:",
        error
      );
      throw error;
    }
  },

  // Récupérer une liste spécifique avec tous ses items
  getListById: async (listId: string): Promise<ShoppingList> => {
    try {
      const response = await axiosInstance.get(
        `${BASE_URL}/${listId}`,
        HEADERS
      );
      return response.data;
    } catch (error) {
      console.error(
        `Erreur lors de la récupération de la liste ${listId}:`,
        error
      );
      throw error;
    }
  },

  // Créer une nouvelle liste de courses
  createList: async (list: ShoppingListCreate): Promise<ShoppingList> => {
    try {
      const response = await axiosInstance.post(
        BASE_URL + "/",
        JSON.stringify(list),
        { ...HEADERS }
      );
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la création de la liste:", error);
      throw error;
    }
  },

  // Mettre à jour une liste de courses (nom, type)
  updateList: async (
    listId: string,
    updates: Partial<ShoppingList>
  ): Promise<ShoppingList> => {
    try {
      const response = await axiosInstance.patch(
        `${BASE_URL}/${listId}`,
        updates,
        { ...HEADERS }
      );
      return response.data;
    } catch (error) {
      console.error(
        `Erreur lors de la mise à jour de la liste ${listId}:`,
        error
      );
      throw error;
    }
  },

  // Supprimer une liste de courses
  deleteList: async (listId: string): Promise<void> => {
    try {
      await axiosInstance.delete(`${BASE_URL}/${listId}`, HEADERS);
    } catch (error) {
      console.error(
        `Erreur lors de la suppression de la liste ${listId}:`,
        error
      );
      throw error;
    }
  },

  // Ajouter un item à une liste
  addItem: async (
    listId: string,
    item: ShoppingItemCreate
  ): Promise<ShoppingItem> => {
    try {
      const response = await axiosInstance.post(
        `${BASE_URL}/${listId}/items`,
        JSON.stringify({ ...item }),
        HEADERS
      );
      return response.data;
    } catch (error) {
      console.error("Erreur lors de l'ajout d'un item:", error);
      throw error;
    }
  },

  // Mettre à jour un item
  updateItem: async (
    listId: string,
    itemId: string,
    updates: Partial<ShoppingItem>
  ): Promise<ShoppingItem> => {
    try {
      const response = await axiosInstance.patch(
        `${BASE_URL}/${listId}/items/${itemId}`,
        updates,
        HEADERS
      );
      return response.data;
    } catch (error) {
      console.error(
        `Erreur lors de la mise à jour de l'item ${itemId}:`,
        error
      );
      throw error;
    }
  },

  // Supprimer un item
  deleteItem: async (listId: string, itemId: string): Promise<void> => {
    try {
      await axiosInstance.delete(
        `${BASE_URL}/${listId}/items/${itemId}`,
        HEADERS
      );
    } catch (error) {
      console.error(
        `Erreur lors de la suppression de l'item ${itemId}:`,
        error
      );
      throw error;
    }
  },

  // Partager une liste avec d'autres utilisateurs
  shareList: async (
    listId: string,
    userIds: string[]
  ): Promise<ShoppingList> => {
    try {
      const response = await axiosInstance.patch(
        `${BASE_URL}/${listId}`,
        { sharedWith: userIds },
        HEADERS
      );
      return response.data;
    } catch (error) {
      console.error(`Erreur lors du partage de la liste ${listId}:`, error);
      throw error;
    }
  },

  // Réinitialiser tous les éléments cochés (les décocher)
  resetCheckedItems: async (listId: string): Promise<ShoppingList> => {
    try {
      const response = await axiosInstance.post(
        `${BASE_URL}/${listId}/reset`,
        {},
        HEADERS
      );
      return response.data;
    } catch (error) {
      console.error(
        `Erreur lors de la réinitialisation de la liste ${listId}:`,
        error
      );
      throw error;
    }
  },

  // Récupérer les membres de la famille pour le partage
  getFamilyMembers: async (): Promise<FamilyMember[]> => {
    try {
      const response = await axiosInstance.get(
        "/users/family/members",
        HEADERS
      );
      return response.data;
    } catch (error) {
      console.error(
        "Erreur lors de la récupération des membres de la famille:",
        error
      );
      throw error;
    }
  },
};
